/**
 * Kick-off times follow the viewer's IP location: the host page's Cloudflare
 * `/cdn-cgi/trace` gives the visitor's country, and that country's zone is
 * used (a UK VPN shows UK times). Where that can't be known — a host not on
 * Cloudflare, a multi-zone country the viewer isn't in — a market `lang` falls
 * back to its market's zone, and anything else to the viewer's OS clock.
 */
const SITE_CLOCKS: Record<string, { timeZone: string; locale: string }> = {
  uk: { timeZone: 'Europe/London', locale: 'en-GB' },
  ie: { timeZone: 'Europe/Dublin', locale: 'en-IE' },
  mx: { timeZone: 'America/Mexico_City', locale: 'es-MX' },
  pe: { timeZone: 'America/Lima', locale: 'es-PE' },
}

/** Market codes that are not language codes, and the language they read. */
const MARKET_LANGS: Record<string, string> = { uk: 'en', ie: 'en', mx: 'es', pe: 'es' }

/**
 * The language to load text and data in. A market code reads its language's
 * content (`uk`/`ie` → English, `mx`/`pe` → Spanish); only the bookmaker lookup keeps the market code,
 * since that is what picks the brand (BetVictor) and the odds format.
 */
export function contentLang(lang: string): string {
  return MARKET_LANGS[lang] ?? lang
}

/** BCP 47 locale for formatting dates in a widget `lang` (`mx` → es-MX). */
export function marketLocale(lang: string | undefined): string | undefined {
  if (!lang) return undefined
  return SITE_CLOCKS[lang]?.locale ?? contentLang(lang)
}

/** IANA zone for a widget `lang`, or undefined to use the viewer's own zone. */
export function siteTimeZone(lang: string | undefined): string | undefined {
  return lang ? SITE_CLOCKS[lang]?.timeZone : undefined
}

// Single-zone countries for browsers without Intl.Locale time-zone data (Firefox).
const COUNTRY_ZONES: Record<string, string> = {
  GB: 'Europe/London', IE: 'Europe/Dublin', ES: 'Europe/Madrid', FR: 'Europe/Paris', DE: 'Europe/Berlin',
  IT: 'Europe/Rome', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels', CH: 'Europe/Zurich', AT: 'Europe/Vienna',
  SE: 'Europe/Stockholm', NO: 'Europe/Oslo', DK: 'Europe/Copenhagen', FI: 'Europe/Helsinki', PL: 'Europe/Warsaw',
  CZ: 'Europe/Prague', GR: 'Europe/Athens', HR: 'Europe/Zagreb', RS: 'Europe/Belgrade', BA: 'Europe/Sarajevo',
  LV: 'Europe/Riga', LT: 'Europe/Vilnius', EE: 'Europe/Tallinn', RO: 'Europe/Bucharest', BG: 'Europe/Sofia',
  HU: 'Europe/Budapest', SK: 'Europe/Bratislava', SI: 'Europe/Ljubljana', MT: 'Europe/Malta', CY: 'Asia/Nicosia',
  IS: 'Atlantic/Reykjavik', UA: 'Europe/Kyiv', TR: 'Europe/Istanbul', IN: 'Asia/Kolkata', JP: 'Asia/Tokyo',
  ZA: 'Africa/Johannesburg', NG: 'Africa/Lagos', AE: 'Asia/Dubai', NZ: 'Pacific/Auckland',
}

/** The zone to use for a country: its only zone, else the viewer's own zone if
 *  it lies in that country, else undefined (multi-zone, viewer elsewhere). */
export function countryTimeZone(country: string, viewerZone?: string): string | undefined {
  const cc = country.toUpperCase()
  let zones: string[] | undefined
  try {
    const loc = new Intl.Locale(`und-${cc}`) as Intl.Locale & { timeZones?: string[]; getTimeZones?: () => string[] }
    zones = loc.getTimeZones?.() ?? loc.timeZones
  } catch {
    zones = undefined
  }
  if (zones?.length === 1) return zones[0]
  if (zones?.length && viewerZone && zones.includes(viewerZone)) return viewerZone
  if (!zones?.length) return COUNTRY_ZONES[cc]
  return undefined
}

let ipZone: Promise<string | undefined> | undefined
/**
 * The visitor's zone from their IP, via the host page's Cloudflare trace
 * (`loc=GB`). One lookup per page, shared by every widget; resolves undefined
 * off Cloudflare, on error, or after a short timeout — never rejects.
 */
export function ipTimeZone(): Promise<string | undefined> {
  if (ipZone) return ipZone
  ipZone = (async () => {
    if (typeof window === 'undefined' || !/^https?:$/.test(window.location.protocol)) return undefined
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 2000)
    try {
      const res = await fetch('/cdn-cgi/trace', { signal: ctrl.signal, credentials: 'omit' })
      if (!res.ok) return undefined
      const country = /^loc=([A-Z]{2})$/m.exec(await res.text())?.[1]
      if (!country || country === 'XX' || country === 'T1') return undefined
      return countryTimeZone(country, Intl.DateTimeFormat().resolvedOptions().timeZone)
    } catch {
      return undefined
    } finally {
      clearTimeout(timer)
    }
  })()
  return ipZone
}

/** A UTC instant → "HH:mm" in `timeZone` (else the site zone, else the
 *  viewer's), '' if invalid. */
export function kickoffTime(instant: Date, lang: string | undefined, timeZone?: string): string {
  if (Number.isNaN(instant.getTime())) return ''
  const clock = lang ? SITE_CLOCKS[lang] : undefined
  return instant.toLocaleTimeString(clock?.locale ?? [], {
    hour: '2-digit',
    minute: '2-digit',
    // 24h everywhere, as the sites always showed (es-MX/es-PE/en-US default to 12h).
    hourCycle: 'h23',
    timeZone: timeZone ?? clock?.timeZone,
  })
}

/** Today as yyyy-mm-dd in `timeZone` (else the site zone, else the viewer's). */
export function todayKeyIn(lang: string | undefined, now = new Date(), timeZone?: string): string {
  // en-CA formats dates as yyyy-mm-dd.
  return now.toLocaleDateString('en-CA', { timeZone: timeZone ?? siteTimeZone(lang) })
}
